import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck } from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import apiClient, { initializeApiClient } from "../../api/apiClient";

export const VehicleOwnersAddVehicle = () => {
  const location = useLocation();
  const { item } = location?.state || {};

  const initialState = {
    license_plate: "",
    fuel_type: "",
    insurance_provider: "",
    policy_number: "",
    vehicle_year: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_length: 0,
    gig_or_ride_share: "",
    commercial: "",
    vehicle_type: "",
    frontside_image: null,
    rearside_image: null,
    driverside_image: null,
    passengerside_image: null,
  };

  const [vehicle, setVehicle] = useState(initialState);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState();
  const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState(null);

  const navigate = useNavigate();

  const handleChange = (key, value) => {
    setVehicle({ ...vehicle, [key]: value });
  };

  const handleImageChange = (key, e) => {
    const imageFile = e.target.files[0];
    const previewUrl = URL.createObjectURL(imageFile);
    setVehicle({ ...vehicle, [key]: { file: imageFile, previewUrl } });
  };

  const fetchVehicleData = async () => {
    setLoading(true);
    try {
      initializeApiClient();
      const res = await apiClient.get("/vehicles/vehicle/");
      if (res.status === 200) {
        setVehicles(res.data);
        if (item) {
          const selectedVehicle = res.data.find((v) => v.id === item.id);
          if (selectedVehicle) {
            setVehicle({
              license_plate: selectedVehicle.license_plate || "",
              fuel_type: selectedVehicle.fuel_type || "",
              insurance_provider: selectedVehicle.insurance_provider || "",
              policy_number: selectedVehicle.policy_number || "",
              vehicle_year: selectedVehicle.vehicle_year || "",
              vehicle_brand: selectedVehicle.vehicle_brand || "",
              vehicle_model: selectedVehicle.vehicle_model || "",
              vehicle_length: selectedVehicle.vehicle_length || 0,
              gig_or_ride_share: selectedVehicle.gig_or_ride_share || "",
              commercial: selectedVehicle.commercial || "",
              vehicle_type: selectedVehicle.vehicle_type || "",
              frontside_image: selectedVehicle.frontside_image || null,
              rearside_image: selectedVehicle.rearside_image || null,
              driverside_image: selectedVehicle.driverside_image || null,
              passengerside_image: selectedVehicle.passengerside_image || null,
            });
            setType(selectedVehicle.vehicle_type);
            setSelectedVehicleTypeId(selectedVehicle.vehicle_type.id);
          }
        }
      } else {
        throw new Error("Failed to fetch vehicle data");
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
      toast.error("Failed to fetch vehicle data");
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const res = await apiClient.get("/vehicles/vehicle-types/");
      if (res.status === 200) {
        console.log("vehicle types res.data", res.data);
        setVehicleTypes(res.data);
      } else {
        throw new Error("Failed to fetch vehicle types");
      }
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      toast.error("Failed to fetch vehicle types");
    }
  };

  useEffect(() => {
    fetchVehicleData();
    fetchVehicleTypes();
  }, [item]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !vehicle.frontside_image ||
      !vehicle.rearside_image ||
      !vehicle.driverside_image ||
      !vehicle.passengerside_image ||
      !selectedVehicleTypeId
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const formData = new FormData();

    Object.keys(vehicle).forEach((key) => {
      if (key.includes("_image")) {
        if (vehicle[key] && vehicle[key].file) {
          formData.append(key, vehicle[key].file);
        }
      } else if (key === "vehicle_type") {
        formData.append(key, selectedVehicleTypeId);
      } else {
        formData.append(key, vehicle[key]);
      }
    });

    setLoading(true);
    try {
      let result;
      if (item) {
        initializeApiClient();
        result = await apiClient.patch(
          `/vehicles/vehicle/${item.id}/`,
          formData
        );
      } else {
        result = await apiClient.post("/vehicles/vehicle/", formData);
      }

      if (result.status !== 200 && result.status !== 201)
        throw new Error("Error updating/creating vehicle");

      toast.success(
        item ? "Vehicle updated successfully" : "Vehicle added successfully"
      );
      await fetchVehicleData();
      navigate("/vehicleownervehicle");
    } catch (error) {
      console.error("Error uploading vehicle:", error);
      toast.error("Error uploading vehicle");
    } finally {
      setLoading(false);
    }
  };

  const toggleRouteType = (key) => {
    setVehicle((prevVehicle) => ({
      ...prevVehicle,
      gig_or_ride_share:
        key === "gig_or_ride_share" ? !prevVehicle.gig_or_ride_share : false,
      commercial: key === "commercial" ? !prevVehicle.commercial : false,
    }));
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiClient.delete(`/vehicles/vehicle/${id}/`);
      if (res.ok) {
        toast.success("Vehicle deleted successfully");
        setVehicles(vehicles.filter((vehicle) => vehicle.id !== id));
      } else {
        throw new Error("Failed to delete vehicle");
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast.error("Failed to delete vehicle");
    }
  };

  const vehicleLength = (item) => {
    const lengths = {
      "Semi Truck": [26, 28, 48, 53],
      "Box Truck": [14, 16, 18, 22, 24, 26, 28],
      Bus: [23, 35, 40, 42],
    };

    return lengths[item.name]?.map((ele) => (
      <div className="mt-2 form-check" key={ele}>
        <input
          className="form-check-input"
          type="radio"
          name="flexRadioDefault"
          id={`flexRadio${ele}`}
          value={ele}
          onClick={(e) => handleChange("vehicle_length", e.target.value)}
        />
        <label className="form-check-label" htmlFor={`flexRadio${ele}`}>
          {`${ele}ft`}
        </label>
      </div>
    ));
  };

  const formatLabel = (label) => {
    // Special cases for side images
    if (label === "driverside_image") return "Driver Side Image";
    if (label === "passengerside_image") return "Passenger Side Image";

    // Default formatting for other labels
    return label
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="site-content h-100">
          <div className="p-4">
            <div className="site-content-body">
              <div className="add-vehicles-images row">
                {/* Image Upload Fields */}
                {[
                  "frontside_image",
                  "rearside_image",
                  "driverside_image",
                  "passengerside_image",
                ].map((side) => (
                  <div className="col-lg-3 col-md-6" key={side}>
                    <div className="add-vehicle-image d-flex flex-column align-items-center justify-content-center">
                      <label htmlFor={side} className="blue-clr fs-5 lh-base">
                        {formatLabel(side)}
                      </label>
                      {vehicle[side] ? (
                        <label htmlFor={side}>
                          <div className="label-box d-flex justify-content-center align-items-center">
                            <img
                              width={"100%"}
                              height={"100%"}
                              style={{ borderRadius: "12px" }}
                              src={vehicle[side]?.previewUrl || vehicle[side]}
                              alt=""
                            />
                          </div>
                        </label>
                      ) : (
                        <label
                          htmlFor={side}
                          className="label-box d-flex justify-content-center align-items-center"
                        >
                          <i className="bi bi-image" />
                        </label>
                      )}
                      <input
                        type="file"
                        className="d-none"
                        id={side}
                        onChange={(e) => handleImageChange(side, e)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="input-fields row mt-2">
                <div className="col-md-6">
                  {/* Input Fields */}
                  {[
                    "license_plate",
                    "fuel_type",
                    "insurance_provider",
                    "policy_number",
                  ].map((field) => (
                    <div
                      className="input-field d-flex flex-column mb-3 fs-5"
                      key={field}
                    >
                      <label className="label blue-clr m-1" htmlFor={field}>
                        {formatLabel(field)}
                      </label>
                      {field === "fuel_type" ? (
                        <select
                          className="form-select w-75 px-2 fs-4 py-1"
                          aria-label="Default select example"
                          value={vehicle.fuel_type}
                          onChange={(e) => handleChange(field, e.target.value)}
                        >
                          <option value="">Select Fuel Type</option>
                          <option value="Gasoline">Gasoline</option>
                          <option value="Diesel">Diesel</option>
                          <option value="Electric">Electric</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={vehicle[field]}
                          onChange={(e) => handleChange(field, e.target.value)}
                          className="form-control m-1 w-75 px-2 py-1 fs-4"
                          id={field}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3 ps-2">
                    <div className="d-flex flex-column me-2 input-field">
                      <label htmlFor="year" className="blue-clr fs-4">
                        Year
                      </label>
                      <input
                        required
                        type="number"
                        value={vehicle?.vehicle_year}
                        onChange={(e) =>
                          handleChange("vehicle_year", e.target.value)
                        }
                        id="year"
                        className="form-control px-2 py-1 fs-4"
                      />
                    </div>

                    <div className="d-flex flex-column me-2 input-field">
                      <label htmlFor="make" className="blue-clr fs-4">
                        Make
                      </label>
                      <input
                        required
                        type="text"
                        value={vehicle?.vehicle_brand}
                        onChange={(e) =>
                          handleChange("vehicle_brand", e.target.value)
                        }
                        id="make"
                        className="form-control px-2 py-1 fs-4"
                      />
                    </div>

                    <div className="d-flex flex-column me-2 input-field">
                      <label htmlFor="model" className="blue-clr fs-4">
                        Model
                      </label>
                      <input
                        required
                        type="text"
                        value={vehicle?.vehicle_model}
                        onChange={(e) =>
                          handleChange("vehicle_model", e.target.value)
                        }
                        id="model"
                        className="form-control px-2 py-1 fs-4"
                      />
                    </div>
                  </div>

                  <div className="vehicle-right-input">
                    <div className="vehicle-type">
                      <label className="blue-clr fs-4 ps-2">Vehicle Type</label>
                      <div className="row">
                        {vehicleTypes.map((item) => (
                          <div key={item.id} className="col-4">
                            <div className="dropdown">
                              <button
                                style={{
                                  borderRadius: "10px",
                                  backgroundColor:
                                    selectedVehicleTypeId === item.id
                                      ? "#F3F3F3"
                                      : "",
                                  cursor: "pointer",
                                }}
                                className="dropdown-toggle py-2 px-3 vehicle-type-btn m-2"
                                type="button"
                                id={`vehicle${item.id}`}
                                value={item.id}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                title={item.name}
                                onClick={() => {
                                  setSelectedVehicleTypeId(item.id);
                                  handleChange("vehicle_type", item.id);
                                }}
                              >
                                <img
                                  src={item.icon_url}
                                  alt={item.name}
                                  className={`img-fluid ${
                                    item.name === "Semi Truck" ? "w-75" : "w-50"
                                  }`}
                                />
                              </button>
                              {["Semi Truck", "Box Truck", "Bus"].includes(
                                item.name
                              ) && (
                                <ul
                                  className="dropdown-menu w-auto border border-secondary rounded-3 p-2"
                                  aria-labelledby={`vehicle${item.id}`}
                                >
                                  <li className="blue-clr text-decoration-underline">
                                    Length
                                  </li>
                                  {vehicleLength(item)}
                                </ul>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="route-type">
                      <label className="blue-clr fs-4 ps-2">Route Type</label>
                      <div className="row">
                        {["gig_or_ride_share", "commercial"].map((route) => (
                          <div className="col-6" key={route}>
                            <button
                              type="button"
                              className={`route-type-btn btn border border-secondary w-100 m-2 ${
                                vehicle[route] ? "selected" : ""
                              }`}
                              onClick={() => toggleRouteType(route)}
                              style={{
                                backgroundColor: vehicle[route]
                                  ? "#007bff"
                                  : "transparent",
                                color: vehicle[route] ? "#fff" : "#000",
                              }}
                            >
                              {formatLabel(route)}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row select-applies">
                {loading ? (
                  <div className="text-center mt-3">
                    <h1>
                      <CircularProgress />
                    </h1>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center">
                    <button
                      type="submit"
                      className="btn_bgnone save-changes blue-clr p-3 fs-4 border-0"
                    >
                      <span className="pe-2">Save Changes</span>
                      <FaCheck />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
